import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Grid, Button,
  Table, TableBody, TableRow, TableCell, TableHead, Avatar, alpha
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupsIcon from '@mui/icons-material/Groups';
import { searchCommittees, getCommitteeMembers } from '../services/api';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

export default function CommitteeDetail() {
  const { cid } = useParams();
  const [committee, setCommittee] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch committee by CID using search API
        const committeeResult = await searchCommittees(`CID:${cid}`, 1, 1);
        if (!committeeResult.hits || committeeResult.hits.length === 0) {
          setError('Committee not found');
          setLoading(false);
          return;
        }
        const committeeData = committeeResult.hits[0];
        setCommittee(committeeData);

        // Fetch committee members (all tenures, current + previous) from
        // the committeemembers index via the dedicated backend endpoint.
        try {
          const memberData = await getCommitteeMembers(cid);
          setMembers(memberData.hits || []);
        } catch (memberErr) {
          console.error('Error fetching committee members:', memberErr);
          // Don't fail the whole page if members can't be loaded
        }
      } catch (err) {
        console.error('Error fetching committee:', err);
        setError('Failed to load committee data');
      }
      setLoading(false);
    };
    fetchData();
  }, [cid]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // YYYY-MM-DD formatter for the members table (ISO, locale-independent).
  const formatIsoDate = (dateStr) => {
    if (!dateStr) return null;
    // Slice handles both "2022-07-26T00:00:00+10:00" and "2022-07-26".
    return String(dateStr).slice(0, 10);
  };

  // "Current" heuristic. The Handbook source uses a year-1 ("0001-01-01")
  // sentinel on ServiceEndDate to mean "open / no real end date" — that's
  // how every current Defence Joint Committee member rendered as Former
  // before this fix. Treat any pre-1900 end date as a sentinel = current.
  // Also tolerate far-future sentinels (year >= 2030) some historic rows
  // use, and a missing/empty end date. Otherwise compare to today (ISO
  // string compare on the YYYY-MM-DD slice — locale-independent).
  const todayIso = new Date().toISOString().slice(0, 10);
  const isCurrentTenure = (member) => {
    const end = member.ServiceEndDate;
    if (!end) return true;
    const endIso = String(end).slice(0, 10);
    const yearNum = parseInt(endIso.slice(0, 4), 10);
    if (!Number.isNaN(yearNum)) {
      if (yearNum <= 1900) return true;   // year-1 / pre-Federation sentinel
      if (yearNum >= 2030) return true;   // far-future sentinel
    }
    return endIso >= todayIso;
  };

  // Bucket + sort: current (by start desc, already from API) then closed (by
  // end desc). Stable sort preserves the start-desc order within "current".
  const sortedMembers = (() => {
    const current = [];
    const previous = [];
    for (const m of members) {
      if (isCurrentTenure(m)) current.push(m);
      else previous.push(m);
    }
    previous.sort((a, b) => {
      const ae = String(a.ServiceEndDate || '').slice(0, 10);
      const be = String(b.ServiceEndDate || '').slice(0, 10);
      if (ae === be) return 0;
      return ae < be ? 1 : -1;
    });
    return [...current, ...previous];
  })();
  const currentCount = sortedMembers.filter(isCurrentTenure).length;
  const previousCount = sortedMembers.length - currentCount;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !committee) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Committee not found'}</Typography>
        <Button component={RouterLink} to="/committees" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Committees
        </Button>
      </Box>
    );
  }

  // Determine chamber color
  const chamberColor = committee.CommitteeChamber === 'Senate' ? '#c62828'
    : committee.CommitteeChamber === 'House' ? '#2e7d32'
    : '#1565c0'; // Joint

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          component={RouterLink}
          to="/committees"
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to Committees
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(chamberColor, 0.08)} 0%, ${alpha(chamberColor, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(chamberColor, 0.15)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <GroupsIcon sx={{ fontSize: 48, color: chamberColor }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: chamberColor,
                mb: 0.5
              }}
            >
              {committee.CommitteeName}
            </Typography>

            {/* Status chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              <Chip
                label={committee.CommitteeChamber}
                size="small"
                sx={{
                  bgcolor: chamberColor,
                  color: 'white',
                  fontWeight: 500
                }}
              />
              <Chip
                label={committee.CommitteeType}
                size="small"
                variant="outlined"
              />
              {committee.Current && (
                <Chip
                  label="Current"
                  size="small"
                  color="success"
                />
              )}
              {!committee.Current && (
                <Chip
                  label="Historical"
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Committee Details */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Committee Details
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '40%' }}>Committee ID</TableCell>
                  <TableCell>{committee.CID}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Chamber</TableCell>
                  <TableCell>{committee.CommitteeChamber}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Type</TableCell>
                  <TableCell>{committee.CommitteeType}</TableCell>
                </TableRow>
                {committee.StartDate && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Start Date</TableCell>
                    <TableCell>{formatDate(committee.StartDate)}</TableCell>
                  </TableRow>
                )}
                {committee.EndDate && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>End Date</TableCell>
                    <TableCell>{formatDate(committee.EndDate)}</TableCell>
                  </TableRow>
                )}
                {committee.ServiceLength && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Service Length</TableCell>
                    <TableCell>{committee.ServiceLength}</TableCell>
                  </TableRow>
                )}
                {committee.ParliamentList && committee.ParliamentList.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Parliaments</TableCell>
                    <TableCell>
                      {committee.ParliamentList.map(p =>
                        `${p}${p === 1 ? 'st' : p === 2 ? 'nd' : p === 3 ? 'rd' : 'th'}`
                      ).join(', ')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Statistics
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, color: 'text.secondary', width: '40%' }}>Total Members</TableCell>
                  <TableCell>{committee.MemberCount || 0}</TableCell>
                </TableRow>
                {committee.CurrentMemberCount > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Current Members</TableCell>
                    <TableCell>{committee.CurrentMemberCount}</TableCell>
                  </TableRow>
                )}
                {committee.WomenCount > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Women Members</TableCell>
                    <TableCell>{committee.WomenCount}</TableCell>
                  </TableRow>
                )}
                {committee.ChairCount > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Chairs</TableCell>
                    <TableCell>{committee.ChairCount}</TableCell>
                  </TableRow>
                )}
                {committee.DeputyChairCount > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Deputy Chairs</TableCell>
                    <TableCell>{committee.DeputyChairCount}</TableCell>
                  </TableRow>
                )}
                {committee.WomenChairCount > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Women Chairs</TableCell>
                    <TableCell>{committee.WomenChairCount}</TableCell>
                  </TableRow>
                )}
                {committee.WomenDeputyChairCount > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary' }}>Women Deputy Chairs</TableCell>
                    <TableCell>{committee.WomenDeputyChairCount}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        {/* Members List — current + previous tenures in one table */}
        {sortedMembers.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                Committee Members ({sortedMembers.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {currentCount} current, {previousCount} previous
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedMembers.map((member, idx) => {
                    const current = isCurrentTenure(member);
                    const displayName = member.Name || member.DisplayName || 'Unknown';
                    return (
                      <TableRow key={member.ParentCommitteeMemberID || `${member.PHID || 'na'}-${idx}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              src={member.PHID ? getParliamentarianPhotoUrl(member.PHID) : undefined}
                              alt={displayName}
                              sx={{ width: 36, height: 36, bgcolor: alpha(chamberColor, 0.15), color: chamberColor, fontSize: '0.85rem' }}
                            >
                              {displayName.split(',')[0]?.charAt(0) || '?'}
                            </Avatar>
                            {member.PHID ? (
                              <Button
                                component={RouterLink}
                                to={`/parliamentarians/${member.PHID}`}
                                size="small"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              >
                                {displayName}
                              </Button>
                            ) : (
                              <Typography variant="body2">{displayName}</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {member.MemberTypes || 'Member'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatIsoDate(member.ServiceStartDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {current ? (
                            <Chip label="Current" size="small" color="success" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {formatIsoDate(member.ServiceEndDate) || '—'}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}

        {/* Related Links */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Related Records
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={RouterLink}
                to={`/questions?q=${encodeURIComponent(`"${committee.CommitteeName}"`)}`}
                variant="outlined"
                size="small"
              >
                View Questions
              </Button>
              <Button
                component={RouterLink}
                to={`/hansard?q=${encodeURIComponent(`"${committee.CommitteeName}"`)}`}
                variant="outlined"
                size="small"
              >
                View Hansard
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
